class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.cartNotification = document.querySelector('cart-notification');
    
    // Check button state on load and when variant/quantity changes
    this.checkButtonState();
    
    // Listen for variant changes
    document.addEventListener('variant:change', () => {
      setTimeout(() => this.checkButtonState(), 100);
    });
    
    // Listen for quantity changes
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.addEventListener('change', () => this.checkButtonState());
      quantityInput.addEventListener('input', () => this.checkButtonState());
    }
  }

  async checkButtonState() {
    const submitButton = this.querySelector('[type="submit"]');
    if (!submitButton) return;

    const quantityInput = this.form.querySelector('input[name="quantity"]');
    const variantIdInput = this.form.querySelector('input[name="id"]');
    const variantId = variantIdInput?.value;

    if (!variantId) return;

    // Get requested quantity (default to 1 if no quantity input exists)
    const requestedQuantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    // Try to get inventory data
    let inventoryQuantity = null;
    let inventoryPolicy = null;

    if (quantityInput) {
      inventoryQuantity = parseInt(quantityInput.getAttribute('data-inventory-quantity'), 10);
      inventoryPolicy = quantityInput.getAttribute('data-inventory-policy');
    }

    if ((inventoryQuantity === null || isNaN(inventoryQuantity)) && variantIdInput) {
      inventoryQuantity = parseInt(variantIdInput.getAttribute('data-inventory-quantity'), 10);
      inventoryPolicy = variantIdInput.getAttribute('data-inventory-policy');
    }

    if ((inventoryQuantity === null || isNaN(inventoryQuantity)) && inventoryPolicy === null) {
      const productDataScript = document.querySelector('[id^="product-data-"]');
      if (productDataScript) {
        try {
          const productData = JSON.parse(productDataScript.textContent);
          const currentVariant = productData.variants?.find(v => String(v.id) === String(variantId));
          if (currentVariant) {
            inventoryQuantity = currentVariant.inventory_quantity;
            inventoryPolicy = currentVariant.inventory_policy;
          }
        } catch (e) {
          console.warn('Product Form - Could not parse product data:', e);
        }
      }
    }

    // If inventory is tracked, check cart
    if (inventoryPolicy !== 'continue' && inventoryPolicy !== null && inventoryQuantity !== null && !isNaN(inventoryQuantity)) {
      try {
        const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await cartRes.json();
        const line = cart.items?.find((item) => String(item.variant_id) === String(variantId));
        const inCartQty = line ? (parseInt(line.quantity, 10) || 0) : 0;
        const desiredTotal = inCartQty + requestedQuantity;

        if (desiredTotal > inventoryQuantity) {
          const allowedToAdd = Math.max(0, inventoryQuantity - inCartQty);
          
          // Disable button
          submitButton.setAttribute('disabled', true);
          
          // Update button text
          const originalText = submitButton.getAttribute('data-original-text') || submitButton.textContent;
          if (!submitButton.getAttribute('data-original-text')) {
            submitButton.setAttribute('data-original-text', originalText);
          }
          
          if (allowedToAdd <= 0) {
            submitButton.textContent = window.variantStrings?.soldOut || 'Sold out';
          } else {
            submitButton.textContent = `Only ${allowedToAdd} available`;
          }
          
          // Update quantity input if it exists
          if (quantityInput && allowedToAdd > 0) {
            quantityInput.value = allowedToAdd;
          }
          
          return;
        }
      } catch (e) {
        console.warn('Product Form - Could not fetch cart for button state check:', e);
      }
    }

    // Enable button if validation passes
    submitButton.removeAttribute('disabled');
    const originalText = submitButton.getAttribute('data-original-text');
    if (originalText) {
      submitButton.textContent = originalText;
      submitButton.removeAttribute('data-original-text');
    }
  }

  async onSubmitHandler(evt) {
    evt.preventDefault();

    // Validate inventory quantity before submission (including quantity already in cart)
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    const variantIdInput = this.form.querySelector('input[name="id"]');
    const variantId = variantIdInput?.value;

    if (!variantId) {
      console.error('Product Form - No variant ID found');
      return;
    }

    // Get requested quantity (default to 1 if no quantity input exists)
    const requestedQuantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    // Try to get inventory data from multiple sources
    let inventoryQuantity = null;
    let inventoryPolicy = null;

    // First, try quantity input
    if (quantityInput) {
      inventoryQuantity = parseInt(quantityInput.getAttribute('data-inventory-quantity'), 10);
      inventoryPolicy = quantityInput.getAttribute('data-inventory-policy');
    }

    // Second, try variant ID input (hidden input)
    if ((inventoryQuantity === null || isNaN(inventoryQuantity)) && variantIdInput) {
      inventoryQuantity = parseInt(variantIdInput.getAttribute('data-inventory-quantity'), 10);
      inventoryPolicy = variantIdInput.getAttribute('data-inventory-policy');
      if (inventoryQuantity !== null && !isNaN(inventoryQuantity)) {
        console.log('Product Form - Got inventory from variant ID input:', {
          variantId,
          inventoryQuantity,
          inventoryPolicy
        });
      }
    }

    // Third, try to get from product data JSON
    if ((inventoryQuantity === null || isNaN(inventoryQuantity)) && inventoryPolicy === null) {
      const productDataScript = document.querySelector('[id^="product-data-"]');
      if (productDataScript) {
        try {
          const productData = JSON.parse(productDataScript.textContent);
          // Find the current variant in the variants array
          const currentVariant = productData.variants?.find(v => String(v.id) === String(variantId));
          if (currentVariant) {
            inventoryQuantity = currentVariant.inventory_quantity;
            inventoryPolicy = currentVariant.inventory_policy;
            console.log('Product Form - Got inventory from product data:', {
              variantId,
              inventoryQuantity,
              inventoryPolicy
            });
          }
        } catch (e) {
          console.warn('Product Form - Could not parse product data:', e);
        }
      }
    }

    console.log('Product Form - Inventory Check (pre-add):', {
      variantId,
      requestedQuantity,
      inventoryQuantity,
      inventoryPolicy,
      hasQuantityInput: !!quantityInput
    });

    // If inventory is tracked (policy != continue) enforce stock considering what's already in cart.
    if (inventoryPolicy !== 'continue' && inventoryPolicy !== null && inventoryQuantity !== null && !isNaN(inventoryQuantity)) {
      try {
        const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await cartRes.json();
        const line = cart.items?.find((item) => String(item.variant_id) === String(variantId));
        const inCartQty = line ? (parseInt(line.quantity, 10) || 0) : 0;
        const desiredTotal = inCartQty + requestedQuantity;

        console.log('Product Form - Cart quantity check:', {
          variantId,
          inCartQty,
          requestedQuantity,
          desiredTotal,
          inventoryQuantity
        });

        if (desiredTotal > inventoryQuantity) {
          // Button should already be disabled by checkButtonState, but ensure it is
          const submitButton = this.querySelector('[type="submit"]');
          if (submitButton) {
            submitButton.setAttribute('disabled', true);
          }
          return;
        }
      } catch (e) {
        // If cart fetch fails, fall back to single-add validation.
        console.warn('Product Form - Could not fetch cart for inventory validation, falling back:', e);
        if (requestedQuantity > inventoryQuantity) {
          // Button should already be disabled by checkButtonState, but ensure it is
          const submitButton = this.querySelector('[type="submit"]');
          if (submitButton) {
            submitButton.setAttribute('disabled', true);
          }
          if (quantityInput) {
            quantityInput.value = inventoryQuantity;
          }
          return;
        }
      }
    }

    this.cartNotification.setActiveElement(document.activeElement);

    const submitButton = this.querySelector('[type="submit"]');

    submitButton.setAttribute('disabled', true);
    submitButton.classList.add('loading');

    const body = JSON.stringify({
      ...JSON.parse(serializeForm(this.form)),
      sections: this.cartNotification.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((parsedState) => {
        // Check for inventory errors in the response
        if (parsedState.description && parsedState.description.includes('available')) {
          // Re-check button state after failed add
          this.checkButtonState();
          submitButton.classList.remove('loading');
          return;
        }

        fetch('/?section_id=cart-notification')
          .then((response) => response.text())
          .then((data) => {
            const parser = new DOMParser();
            const htmlDoc = parser.parseFromString(data, 'text/html');
            const cart = htmlDoc.querySelector("cart-notification #cart-notification");

            const currentCartNotification = document.querySelector("cart-notification #cart-notification");

            this.cartNotification.renderCart(parsedState, {
              cartNotification: currentCartNotification,
              text: cart
            });
            
            // Re-check button state after successful add (in case inventory is now exhausted)
            this.checkButtonState();
          })
          .finally(() => {
            submitButton.classList.remove('loading');
          });
      })
      .catch((e) => {
        console.error(e);
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('disabled');
      });
  }
}

customElements.define('product-form', ProductForm);
